/**
 * Local-first mood store.
 *
 * The device is the working copy. Every check-in is written to localStorage
 * synchronously and is immediately readable; syncing to Supabase happens in the
 * background and is allowed to fail.
 *
 * Why: the remote table may not exist yet, the project may be paused, the phone
 * may be in a gym basement. In every one of those cases the user still gets a
 * working feature and never loses an entry — instead of tapping "Check In" and
 * getting an error they can't do anything about.
 *
 * Sync model: each record carries a client-generated `id`. Once it reaches the
 * server we record `remoteId` and `synced: true`. Remote records already
 * represented locally are skipped on merge, so a round trip can't duplicate an
 * entry.
 */

import { supabase } from "./supabase";
import { mergeCheckins } from "../utils/mood";

const KEY = (userId) => `mood_checkins_${userId || "anon"}`;
const MAX_LOCAL = 400; // ~a year of daily + session check-ins

function readLocal(userId) {
  try {
    const raw = localStorage.getItem(KEY(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(userId, records) {
  try {
    const trimmed = [...records]
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .slice(-MAX_LOCAL);
    localStorage.setItem(KEY(userId), JSON.stringify(trimmed));
    return trimmed;
  } catch {
    return records; // storage full / private mode — keep working in memory
  }
}

function clientId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
}

/**
 * Save a check-in. Always succeeds locally and returns immediately; the remote
 * write is best-effort and never surfaces an error to the caller.
 */
export function saveCheckin(userId, checkin) {
  const record = {
    id: clientId(),
    user_id: userId,
    date: checkin.date,
    mood: checkin.mood,
    energy: checkin.energy ?? null,
    note: checkin.note ?? null,
    context: checkin.context || "daily",
    workout_id: checkin.workoutId ?? null,
    created_at: new Date().toISOString(),
    synced: false,
  };

  const local = readLocal(userId);
  writeLocal(userId, [...local, record]);

  // Fire-and-forget; failure just leaves it pending for the next sync.
  pushOne(userId, record).catch(() => {});

  return record;
}

async function pushOne(userId, record) {
  const { data, error } = await supabase
    .from("mood_checkins")
    .insert({
      user_id: userId,
      date: record.date,
      mood: record.mood,
      energy: record.energy,
      note: record.note,
      context: record.context,
      workout_id: record.workout_id,
      created_at: record.created_at,
    })
    .select()
    .single();
  if (error) throw error;

  const local = readLocal(userId).map((r) =>
    r.id === record.id ? { ...r, synced: true, remoteId: data.id } : r
  );
  writeLocal(userId, local);
  return data;
}

/** Push everything still pending. Safe to call often; silent on failure. */
export async function syncPending(userId) {
  const pending = readLocal(userId).filter((r) => !r.synced);
  if (pending.length === 0) return { synced: 0 };
  let synced = 0;
  for (const record of pending) {
    try {
      await pushOne(userId, record);
      synced++;
    } catch {
      break; // backend unavailable — stop and retry later
    }
  }
  return { synced };
}

/**
 * Everything this device already knows, synchronously.
 *
 * loadCheckins() has to await a network round trip before it can resolve, and
 * during that window the app cannot tell whether today has been answered. That
 * ambiguity is not survivable for the daily ritual — guessing wrong means
 * either asking someone the same question twice or silently skipping the only
 * screen that matters. localStorage is the working copy, so read it directly.
 */
export function localCheckins(userId) {
  return readLocal(userId);
}

/**
 * All known check-ins. Returns local data immediately-consistent, and folds in
 * anything the server has that we don't (e.g. entries from another device).
 * Never throws.
 */
export async function loadCheckins(userId) {
  const local = readLocal(userId);
  if (!userId) return local;

  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}-${String(cutoff.getDate()).padStart(2, "0")}`;

    const { data, error } = await supabase
      .from("mood_checkins")
      .select("*")
      .eq("user_id", userId)
      .gte("date", cutoffStr)
      .order("created_at", { ascending: true });
    if (error) throw error;

    const merged = mergeCheckins(local, data || []);
    writeLocal(userId, merged);
    syncPending(userId).catch(() => {});
    return merged;
  } catch {
    // Table missing, project paused, offline — local data is still the truth.
    return local;
  }
}

/**
 * Attach a note to a reading that has already been written.
 *
 * The ritual writes the reading the moment mood and energy are both known — so
 * that walking away mid-sequence cannot lose it — and only then offers the
 * optional note. Which meant, until now, that anything a patient typed into
 * that field was rendered, run past the crisis matcher, and then thrown away:
 * the one free-text disclosure in the product, discarded. The crisis card
 * still appeared, so the failure was invisible.
 *
 * Local write is synchronous and authoritative. The remote update is
 * best-effort and only possible for a record that has already synced; anything
 * else stays pending and carries its note up on the next push.
 */
export function updateCheckinNote(userId, id, note) {
  const local = readLocal(userId);
  const record = local.find((r) => r.id === id);
  if (!record) return null;

  const updated = { ...record, note: note || null };
  writeLocal(userId, local.map((r) => (r.id === id ? updated : r)));

  if (updated.synced && updated.remoteId) {
    supabase
      .from("mood_checkins")
      .update({ note: updated.note })
      .eq("id", updated.remoteId)
      .then(null, () => {});
  }

  return updated;
}

/** Clear on sign-out so data never crosses accounts. */
export function clearLocalCheckins(userId) {
  try { localStorage.removeItem(KEY(userId)); } catch { /* ignore */ }
}
